/**
 * domains/journal/calibration/candidates.ts — MER Algo Footprints v7.1H
 *
 * Calibration candidate detector.
 * Surfaces structured CalibrationCandidate objects from attributed records.
 * Used for threshold tuning recommendations and dashboard display.
 */

import { AuditRecord } from '../../../shared/types';
import { AttributedRecord, CalibrationCandidate, CalibrationCandidateKind } from './types';

const MIN_CONFIDENCE   = 65;
const STRONG_WIN_R     = 1.5;
const CLEAR_LOSS_R     = -0.8;
const MIN_PATTERN_SIZE = 3;    // minimum records to declare a pattern

// ─── Per-record candidates (already classified in attribution.ts) ──────────────

export function extractRecordCandidates(
  attributed: AttributedRecord[],
  originals:  AuditRecord[],
): CalibrationCandidate[] {
  const candidates: CalibrationCandidate[] = [];
  const originalMap = new Map<string, AuditRecord>(originals.map(r => [r.id, r]));

  for (const ar of attributed) {
    if (!ar.is_calibration_candidate || !ar.calibration_kind) continue;
    const orig = originalMap.get(ar.audit_id);
    // Use setup2_score when available — not raw confidence
    const score = ar.setup2_score ?? ar.confidence;
    candidates.push({
      kind:          ar.calibration_kind,
      symbol:        ar.symbol,
      audit_id:      ar.audit_id,
      reason:        ar.calibration_reason ?? '',
      current_value: score,
      threshold:     MIN_CONFIDENCE,
      delta:         Math.abs(score - MIN_CONFIDENCE),
      outcome:       ar.outcome,
      realized_r:    ar.realized_r,
      regime:        ar.regime,
      setup_type:    ar.setup_type,
      detected_at:   orig?.createdAt ?? Date.now(),
    });
  }

  return candidates;
}

// ─── Pattern-level candidates (require aggregation across records) ─────────────

/** Over-filter: ≥3 consecutive filtered records that would have won */
export function detectOverFilterPattern(
  attributed: AttributedRecord[],
  originals:  AuditRecord[],
  bySetup:    string,
): CalibrationCandidate[] {
  const originalMap = new Map<string, AuditRecord>(originals.map(r => [r.id, r]));
  const candidates: CalibrationCandidate[] = [];

  // Group by setup type
  const bySetupMap = new Map<string, AttributedRecord[]>();
  for (const r of attributed) {
    const arr = bySetupMap.get(r.setup_type) ?? [];
    arr.push(r);
    bySetupMap.set(r.setup_type, arr);
  }

  for (const [setup, recs] of bySetupMap) {
    const filteredWins = recs.filter(r =>
      r.category === 'FALSE_NEGATIVE' && r.realized_r !== null && r.realized_r >= STRONG_WIN_R
      && !r.is_no_valid_setup);  // exclude NVS — those are correct suppressions

    if (filteredWins.length >= MIN_PATTERN_SIZE) {
      const avgR = filteredWins.reduce((s, r) => s + r.realized_r!, 0) / filteredWins.length;
      const orig = originalMap.get(filteredWins[0].audit_id);
      candidates.push({
        kind:          'OVER_FILTER',
        symbol:        filteredWins[0].symbol,
        audit_id:      filteredWins[0].audit_id,
        reason:        `${filteredWins.length} filtered ${setup} setups would have won (avg R=${avgR.toFixed(2)}) — threshold may be too strict`,
        current_value: filteredWins.reduce((s, r) => s + (r.setup2_score ?? r.confidence), 0) / filteredWins.length,
        threshold:     MIN_CONFIDENCE,
        delta:         MIN_CONFIDENCE - (filteredWins.reduce((s, r) => s + (r.setup2_score ?? r.confidence), 0) / filteredWins.length),
        outcome:       'would-have-won',
        realized_r:    avgR,
        regime:        filteredWins[0].regime,
        setup_type:    setup,
        detected_at:   orig?.createdAt ?? Date.now(),
      });
    }
  }

  return candidates;
}

/** Under-filter: ≥3 actionable setups that failed in same regime/setup context */
export function detectUnderFilterPattern(
  attributed: AttributedRecord[],
  originals:  AuditRecord[],
): CalibrationCandidate[] {
  const originalMap = new Map<string, AuditRecord>(originals.map(r => [r.id, r]));
  const candidates: CalibrationCandidate[] = [];

  // Group by setup + regime
  const byContext = new Map<string, AttributedRecord[]>();
  for (const r of attributed) {
    const key = `${r.setup_type}::${r.regime}`;
    const arr = byContext.get(key) ?? [];
    arr.push(r);
    byContext.set(key, arr);
  }

  for (const [key, recs] of byContext) {
    const [setup_type, regime] = key.split('::');
    const fpRecs = recs.filter(r =>
      r.category === 'FALSE_POSITIVE' && r.realized_r !== null && r.realized_r <= CLEAR_LOSS_R);

    if (fpRecs.length >= MIN_PATTERN_SIZE) {
      const avgR = fpRecs.reduce((s, r) => s + r.realized_r!, 0) / fpRecs.length;
      const orig = originalMap.get(fpRecs[0].audit_id);
      candidates.push({
        kind:          'UNDER_FILTER',
        symbol:        fpRecs[0].symbol,
        audit_id:      fpRecs[0].audit_id,
        reason:        `${fpRecs.length} actionable ${setup_type} setups in ${regime} regime failed (avg R=${avgR.toFixed(2)}) — threshold too low for this context`,
        current_value: fpRecs.reduce((s, r) => s + (r.setup2_score ?? r.confidence), 0) / fpRecs.length,
        threshold:     MIN_CONFIDENCE,
        delta:         fpRecs.reduce((s, r) => s + (r.setup2_score ?? r.confidence), 0) / fpRecs.length - MIN_CONFIDENCE,
        outcome:       'repeated-loss',
        realized_r:    avgR,
        regime,
        setup_type,
        detected_at:   orig?.createdAt ?? Date.now(),
      });
    }
  }

  return candidates;
}

// ─── Full candidate extraction ────────────────────────────────────────────────

export function extractAllCalibrationCandidates(
  attributed: AttributedRecord[],
  originals:  AuditRecord[],
): CalibrationCandidate[] {
  const perRecord  = extractRecordCandidates(attributed, originals);
  const overFilter = detectOverFilterPattern(attributed, originals, '');
  const underFilter = detectUnderFilterPattern(attributed, originals);

  return [
    ...perRecord,
    ...overFilter,
    ...underFilter,
  ].sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));  // largest deltas first
}

export function summarizeCandidates(candidates: CalibrationCandidate[]): {
  threshold_miss_lower:    number;
  threshold_miss_upper:    number;
  false_positive_patterns: number;
  false_negative_patterns: number;
  over_filter:             number;
  under_filter:            number;
} {
  const count = (k: CalibrationCandidateKind) => candidates.filter(c => c.kind === k).length;
  return {
    threshold_miss_lower:    count('THRESHOLD_MISS_LOWER'),
    threshold_miss_upper:    count('THRESHOLD_MISS_UPPER'),
    false_positive_patterns: count('FALSE_POSITIVE_PATTERN'),
    false_negative_patterns: count('FALSE_NEGATIVE_PATTERN'),
    over_filter:             count('OVER_FILTER'),
    under_filter:            count('UNDER_FILTER'),
  };
}
